import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authApi } from '../../services/api/authApi';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'success' | 'error' | 'idle' | 'pending'>('idle');
  const [userEmail, setUserEmail] = useState<string>('');
  const [alreadyVerified, setAlreadyVerified] = useState(false);

  const verifyEmailToken = async (token: string) => {
    try {
      setIsLoading(true);
      
      // Call the backend API to verify the email
      const response: any = await authApi.verifyEmail(token);
      
      if (response) {
        setVerificationStatus('success');
        const successMessage = response.message || 'Email verified successfully! Welcome to DigiCartt';
        toast.success(successMessage);
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      setVerificationStatus('error');
      
      const errorMessage = error.response?.data?.message || 'Email verification failed.';
      toast.error(errorMessage);
      
      // Redirect to signup page after 5 seconds
      setTimeout(() => {
        navigate('/signup');
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerificationEmail = async () => {
    if (!userEmail) {
      toast.error('Email address not found. Please sign up again.');
      navigate('/signup');
      return;
    }

    try {
      setIsResending(true);
      await authApi.resendVerification(userEmail);
      toast.success('Verification email sent successfully! Please check your inbox.');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to resend verification email.';
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    const token = searchParams.get('token');
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const email = searchParams.get('email');
    const alreadyVerifiedParam = searchParams.get('already_verified');

    // Set user email if available
    if (email) {
      setUserEmail(decodeURIComponent(email));
    }

    // If we have success or error parameters, show the result
    if (success === 'true') {
      setVerificationStatus('success');
      
      if (alreadyVerifiedParam === 'true') {
        setAlreadyVerified(true);
        toast.info('Your email is already verified!');
      } else {
        toast.success('Email verified successfully! Welcome to DigiCartt');
      }
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } else if (error) {
      setVerificationStatus('error');
      
      let errorMessage = 'Email verification failed.';
      switch (error) {
        case 'invalid_token':
          errorMessage = 'Invalid verification token.';
          break;
        case 'invalid_or_expired_token':
          errorMessage = 'Verification token is invalid or has expired.';
          break;
        case 'server_error':
          errorMessage = 'Server error occurred during verification.';
          break;
        default:
          errorMessage = 'Email verification failed.';
      }
      
      toast.error(errorMessage);
      
      // Don't auto-redirect on error, let user choose what to do
    } else if (token) {
      // If there's a token in the URL, call the backend API
      verifyEmailToken(token);
    } else if (email) {
      // If there's an email but no token, show the verification pending state
      setVerificationStatus('pending');
      toast.info('Please check your email for the verification link.');
    } else {
      // No token, success, error, or email - invalid access
      setVerificationStatus('error');
      toast.error('Invalid verification link.');
      
      setTimeout(() => {
        navigate('/signup');
      }, 3000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  const handleResendVerification = () => {
    resendVerificationEmail();
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToSignup = () => {
    navigate('/signup');
  };

  if (isLoading) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card" style={{marginBottom: "100px"}}>
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h4 className="mt-3">Verifying your email...</h4>
                <p className="text-muted">Please wait while we verify your email address.</p>
                <p className="text-muted small">This should only take a moment.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card" style={{marginBottom: "100px"}}>
            <div className="card-body text-center">
              {verificationStatus === 'success' && (
                <>
                  <div className="text-success mb-3">
                    <i className="fas fa-check-circle fa-3x"></i>
                  </div>
                  <h3 className="text-success">
                    {alreadyVerified ? 'Email Already Verified!' : 'Email Verified Successfully!'}
                  </h3>
                  <p className="text-muted">
                    {alreadyVerified 
                      ? 'Your email address is already verified. You can log in to your account.'
                      : 'Your email has been verified. You can now log in to your account.'
                    }
                  </p>
                  <div className="mt-4">
                    <button 
                      className="btn btn-primary me-2" 
                      onClick={handleGoToLogin}
                    >
                      Go to Login
                    </button>
                  </div>
                  <p className="text-muted mt-3">
                    <small>You will be redirected to the login page in a few seconds...</small>
                  </p>
                </>
              )}

              {verificationStatus === 'pending' && (
                <>
                  <div className="text-warning mb-3">
                    <i className="fas fa-envelope fa-3x"></i>
                  </div>
                  <h3 className="text-warning">Check Your Email</h3>
                  <p className=" text-muted">
                    We've sent a verification link to <strong>{userEmail}</strong>
                  </p>
                  <p className="text-muted">
                    Please check your email inbox and click the verification link to activate your account.
                  </p>
                  
                  <div className="mt-4 p-3 bg-light rounded">
                    <p className="text-muted mb-2">
                      <strong>Email:</strong> {userEmail}
                    </p>
                    <button 
                      className="btn btn-outline-primary btn-sm" 
                      onClick={handleResendVerification}
                      disabled={isResending}
                    >
                      {isResending ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Sending...
                        </>
                      ) : (
                        'Resend Verification Email'
                      )}
                    </button>
                  </div>
                  
                  <div className="mt-4">
                    <button 
                      className="btn btn-primary me-2" 
                      onClick={handleGoToLogin}
                    >
                      Go to Login
                    </button>
                    <button 
                      className="btn btn-outline-secondary" 
                      onClick={handleGoToSignup}
                    >
                      Sign Up Again
                    </button>
                  </div>
                </>
              )}

              {verificationStatus === 'error' && (
                <>
                  <div className="text-danger mb-3">
                    <i className="fas fa-exclamation-triangle fa-3x"></i>
                  </div>
                  <h3 className="font_two text-danger">Verification Failed</h3>
                  <p className="font_two text-muted">
                    We couldn't verify your email address. This could be because:
                  </p>
                  <ul className="font_two text-start text-muted">
                    <li>The verification link has expired (links expire after 30 minutes)</li>
                    <li>The link has already been used</li>
                    <li>The link is invalid or corrupted</li>
                  </ul>
                  
                  {userEmail && (
                    <div className="mt-4 p-3 bg-light rounded">
                      <p className="text-muted mb-2">
                        <strong>Email:</strong> {userEmail}
                      </p>
                      <button 
                        className="btn btn-outline-primary btn-sm" 
                        onClick={handleResendVerification}
                        disabled={isResending}
                      >
                        {isResending ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : (
                          'Resend Verification Email'
                        )}
                      </button>
                    </div>
                  )}
                  
                  <div className="mt-4">
                    <button 
                      className="font_two btn btn-primary me-2" 
                      onClick={handleGoToSignup}
                    >
                      Sign Up Again
                    </button>
                    <button 
                      className="font_two btn btn-outline-secondary" 
                      onClick={handleGoToLogin}
                    >
                      Go to Login
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;

